import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './GuestList.css';
import { weddingConfig } from '../config/weddingConfig';
import { getApiUrl, getAuthHeaders, apiRequest } from '../config/apiConfig';

// Password for accessing the guest list - must be set in environment variable REACT_APP_GUEST_LIST_PASSWORD
// Note: Environment variables in React must be prefixed with REACT_APP_ and require a server restart
const GUEST_LIST_PASSWORD = process.env.REACT_APP_GUEST_LIST_PASSWORD;

// Debug logging (remove in production)
if (process.env.NODE_ENV === 'development') {
  console.log('Environment check:', {
    hasPassword: !!GUEST_LIST_PASSWORD,
    passwordLength: GUEST_LIST_PASSWORD ? GUEST_LIST_PASSWORD.length : 0,
    allEnvVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
  });
}

if (!GUEST_LIST_PASSWORD) {
  console.error('REACT_APP_GUEST_LIST_PASSWORD environment variable is not set');
  console.error('Please create a .env file in the client directory with: REACT_APP_GUEST_LIST_PASSWORD=your_password');
}

const GuestList = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if already authenticated (stored in sessionStorage)
    const auth = sessionStorage.getItem('guestListAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
      fetchGuests();
    }
  }, []);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (!GUEST_LIST_PASSWORD) {
      setError('Guest list password is not configured. Please contact the administrator.');
      return;
    }
    if (password === GUEST_LIST_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('guestListAuth', 'true');
      fetchGuests();
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  const fetchGuests = async () => {
    setLoading(true);
    setError('');
    try {
      const authHeaders = await getAuthHeaders();
      const response = await apiRequest({
        method: 'get',
        url: getApiUrl('api/rsvp/all'),
        headers: authHeaders
      });
      if (response.data && response.data.success) {
        setGuests(response.data.guests);
      } else {
        setError('Failed to load guest data');
      }
    } catch (err) {
      console.error('Error fetching guests:', err);
      setError('Error loading guest data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to convert backend format (rsvpFor, attending) to person1Attending/person2Attending
  // Handles special encoding: 'person1_only' = person1 attending, person2 declined
  // 'person2_only' = person2 attending, person1 declined
  // 'person1' = person1 attending, person2 not RSVP'd
  // 'person2' = person2 attending, person1 not RSVP'd
  const convertBackendToCoupleRSVP = (rsvpFor, attending) => {
    // If attending is null/undefined, no one has RSVP'd
    if (attending === null || attending === undefined) {
      return { person1Attending: null, person2Attending: null };
    }
    
    // If attending is false, both declined
    if (attending === false) {
      return { person1Attending: false, person2Attending: false };
    }
    
    // If attending is true, check rsvpFor
    if (attending === true) {
      if (rsvpFor === 'both') {
        return { person1Attending: true, person2Attending: true };
      }
      if (rsvpFor === 'person1_only') {
        // Person1 attending, person2 declined
        return { person1Attending: true, person2Attending: false };
      }
      if (rsvpFor === 'person2_only') {
        // Person2 attending, person1 declined
        return { person1Attending: false, person2Attending: true };
      }
      if (rsvpFor === 'person1') {
        // Person1 attending, person2 not RSVP'd
        return { person1Attending: true, person2Attending: null };
      }
      if (rsvpFor === 'person2') {
        // Person2 attending, person1 not RSVP'd
        return { person1Attending: null, person2Attending: true };
      }
    }
    
    return { person1Attending: null, person2Attending: null };
  };

  const getRSVPStatus = (guest, day) => {
    const rsvp = guest.rsvp?.[day];
    // Check if RSVP exists and has been set (attending is explicitly true or false)
    // If attending is null or undefined, the guest has not RSVP'd
    if (!rsvp || (rsvp.attending !== true && rsvp.attending !== false)) {
      return { status: 'Not RSVP\'d', guests: 0, attendingPerson: null, detailedStatus: null };
    }
    
    if (guest.isCouple) {
      // For couples, show detailed status
      const converted = convertBackendToCoupleRSVP(rsvp.rsvpFor, rsvp.attending);
      const p1Status = converted.person1Attending;
      const p2Status = converted.person2Attending;
      const p1Name = guest.person1.split(' ')[0]; // First name only
      const p2Name = guest.person2.split(' ')[0]; // First name only
      
      const statusParts = [];
      if (p1Status === true) {
        statusParts.push({ name: p1Name, status: 'Attending', statusClass: 'status-attending' });
      } else if (p1Status === false) {
        statusParts.push({ name: p1Name, status: 'Not Attending', statusClass: 'status-not-attending' });
      } else {
        statusParts.push({ name: p1Name, status: 'Not RSVP\'d', statusClass: 'status-not-rsvpd' });
      }
      
      if (p2Status === true) {
        statusParts.push({ name: p2Name, status: 'Attending', statusClass: 'status-attending' });
      } else if (p2Status === false) {
        statusParts.push({ name: p2Name, status: 'Not Attending', statusClass: 'status-not-attending' });
      } else {
        statusParts.push({ name: p2Name, status: 'Not RSVP\'d', statusClass: 'status-not-rsvpd' });
      }
      
      // Determine overall status for display
      let overallStatus = 'Not RSVP\'d';
      let attendingPerson = null;
      if (rsvp.attending === true) {
        overallStatus = 'Attending';
        if (rsvp.rsvpFor === 'both') {
          attendingPerson = 'Both';
        } else if (rsvp.rsvpFor === 'person1' || rsvp.rsvpFor === 'person1_only') {
          attendingPerson = p1Name;
        } else if (rsvp.rsvpFor === 'person2' || rsvp.rsvpFor === 'person2_only') {
          attendingPerson = p2Name;
        }
      } else if (rsvp.attending === false) {
        overallStatus = 'Not Attending';
      }
      
      return {
        status: overallStatus,
        guests: rsvp.guests || 0,
        attendingPerson: attendingPerson,
        detailedStatus: statusParts
      };
    }
    
    // Single guest
    if (rsvp.attending === true) {
      return { 
        status: 'Attending', 
        guests: rsvp.guests || 1,
        attendingPerson: null,
        detailedStatus: null
      };
    }
    // rsvp.attending === false
    return { status: 'Not Attending', guests: 0, attendingPerson: null, detailedStatus: null };
  };

  const getTotalAttending = (day) => {
    return guests.reduce((total, guest) => {
      const rsvp = getRSVPStatus(guest, day);
      return total + (rsvp.status === 'Attending' ? rsvp.guests : 0);
    }, 0);
  };

  const getTotalResponded = (day) => {
    return guests.reduce((total, guest) => {
      const rsvp = guest.rsvp?.[day];
      // Count as responded if attending is explicitly true or false (not null/undefined)
      if (rsvp && (rsvp.attending === true || rsvp.attending === false)) {
        if (guest.isCouple) {
          // For couples, count based on rsvpFor field
          if (rsvp.rsvpFor === 'both') {
            return total + 2; // Both people responded
          } else if (rsvp.rsvpFor === 'person1_only' || rsvp.rsvpFor === 'person2_only') {
            return total + 2; // Both people responded (one attending, one declined)
          } else if (rsvp.rsvpFor === 'person1' || rsvp.rsvpFor === 'person2') {
            return total + 1; // Only one person responded (the other hasn't RSVP'd)
          } else {
            // rsvpFor is null - this means both declined
            // For consistency, count as both declined (2 people responded with "no")
            return total + 2;
          }
        } else {
          // Single guest
          return total + 1;
        }
      }
      return total;
    }, 0);
  };

  const getTotalPeople = () => {
    return guests.reduce((total, guest) => {
      // Count as 2 people if it's a couple, 1 if single
      return total + (guest.isCouple ? 2 : 1);
    }, 0);
  };

  if (!isAuthenticated) {
    return (
      <div className="guest-list-container">
        <div className="password-form">
          <h2 style={{ fontFamily: weddingConfig.design.fonts.heading, color: weddingConfig.design.colors.primary }}>
            Enter Password
          </h2>
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              style={{
                fontFamily: weddingConfig.design.fonts.body,
                padding: '10px',
                fontSize: '1rem',
                border: `2px solid ${weddingConfig.design.colors.primary}`,
                borderRadius: '4px',
                marginBottom: '15px',
                width: '250px'
              }}
              autoFocus
            />
            {error && <p style={{ color: '#dc3545', marginBottom: '15px' }}>{error}</p>}
            <button
              type="submit"
              style={{
                fontFamily: weddingConfig.design.fonts.body,
                padding: '10px 20px',
                backgroundColor: weddingConfig.design.colors.primary,
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Submit
            </button>
          </form>
          <button
            onClick={() => navigate('/')}
            style={{
              fontFamily: weddingConfig.design.fonts.body,
              marginTop: '20px',
              padding: '8px 16px',
              backgroundColor: 'transparent',
              color: weddingConfig.design.colors.primary,
              border: `2px solid ${weddingConfig.design.colors.primary}`,
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="guest-list-container">
      <div className="container">
        <div className="guest-list-header">
          <h1 style={{ fontFamily: weddingConfig.design.fonts.heading, color: weddingConfig.design.colors.primary }}>
            Guest List & RSVPs
          </h1>
          <button
            onClick={() => {
              sessionStorage.removeItem('guestListAuth');
              navigate('/');
            }}
            style={{
              fontFamily: weddingConfig.design.fonts.body,
              padding: '8px 16px',
              backgroundColor: 'transparent',
              color: weddingConfig.design.colors.primary,
              border: `2px solid ${weddingConfig.design.colors.primary}`,
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>

        {loading && <p style={{ textAlign: 'center' }}>Loading guest data...</p>}
        {error && <p style={{ textAlign: 'center', color: '#dc3545' }}>{error}</p>}

        {!loading && !error && (
          <>
            <div className="summary-cards">
              <div className="summary-card">
                <h3 style={{ fontFamily: weddingConfig.design.fonts.heading, color: weddingConfig.design.colors.primary }}>
                  Day 1 Summary
                </h3>
                <p style={{ fontFamily: weddingConfig.design.fonts.body }}>
                  <strong>Total Attending:</strong> {getTotalAttending('day1')} guests
                </p>
                <p style={{ fontFamily: weddingConfig.design.fonts.body }}>
                  <strong>Responded:</strong> {getTotalResponded('day1')} / {getTotalPeople()}
                </p>
              </div>
              <div className="summary-card">
                <h3 style={{ fontFamily: weddingConfig.design.fonts.heading, color: weddingConfig.design.colors.primary }}>
                  Day 2 Summary
                </h3>
                <p style={{ fontFamily: weddingConfig.design.fonts.body }}>
                  <strong>Total Attending:</strong> {getTotalAttending('day2')} guests
                </p>
                <p style={{ fontFamily: weddingConfig.design.fonts.body }}>
                  <strong>Responded:</strong> {getTotalResponded('day2')} / {getTotalPeople()}
                </p>
              </div>
            </div>

            <div className="table-container">
              <table className="guest-table">
                <thead>
                  <tr>
                    <th style={{ fontFamily: weddingConfig.design.fonts.heading }}>Guest(s)</th>
                    <th style={{ fontFamily: weddingConfig.design.fonts.heading }}>Type</th>
                    <th style={{ fontFamily: weddingConfig.design.fonts.heading }}>Day 1 Status</th>
                    <th style={{ fontFamily: weddingConfig.design.fonts.heading }}>Day 1 Guests</th>
                    <th style={{ fontFamily: weddingConfig.design.fonts.heading }}>Day 2 Status</th>
                    <th style={{ fontFamily: weddingConfig.design.fonts.heading }}>Day 2 Guests</th>
                  </tr>
                </thead>
                <tbody>
                  {guests.map((guest) => {
                    const day1Status = getRSVPStatus(guest, 'day1');
                    const day2Status = getRSVPStatus(guest, 'day2');
                    return (
                      <tr key={guest.id} className={guest.isCouple ? 'couple-row' : ''}>
                        <td style={{ fontFamily: weddingConfig.design.fonts.body }}>
                          {guest.person1}
                          {guest.person2 && ` & ${guest.person2}`}
                        </td>
                        <td style={{ fontFamily: weddingConfig.design.fonts.body }}>
                          {guest.isCouple ? 'Couple' : 'Single'}
                        </td>
                        <td style={{ fontFamily: weddingConfig.design.fonts.body }}>
                          <div className="status-cell">
                            {day1Status.detailedStatus ? (
                              <div className="detailed-status">
                                {day1Status.detailedStatus.map((item, idx) => (
                                  <span key={idx} className={`status-badge ${item.statusClass}`}>
                                    {item.name}: {item.status}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <>
                                <span className={`status-badge status-${day1Status.status.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')}`}>
                                  {day1Status.status}
                                </span>
                                {guest.isCouple && day1Status.attendingPerson && (
                                  <span className="attending-person">
                                    ({day1Status.attendingPerson})
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                        <td style={{ fontFamily: weddingConfig.design.fonts.body }}>
                          {day1Status.guests}
                        </td>
                        <td style={{ fontFamily: weddingConfig.design.fonts.body }}>
                          <div className="status-cell">
                            {day2Status.detailedStatus ? (
                              <div className="detailed-status">
                                {day2Status.detailedStatus.map((item, idx) => (
                                  <span key={idx} className={`status-badge ${item.statusClass}`}>
                                    {item.name}: {item.status}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <>
                                <span className={`status-badge status-${day2Status.status.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '')}`}>
                                  {day2Status.status}
                                </span>
                                {guest.isCouple && day2Status.attendingPerson && (
                                  <span className="attending-person">
                                    ({day2Status.attendingPerson})
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                        <td style={{ fontFamily: weddingConfig.design.fonts.body }}>
                          {day2Status.guests}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GuestList;

